import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Egg, Trash2, Plus } from "lucide-react";
import { api, errText } from "../../lib/api";
import { PROVINCE_NAME } from "../../lib/constants";
import { useLanguage } from "../../context/LanguageContext";
import { SPECIES_FR } from "../litters/LitterCard";
import { Switch } from "../ui/switch";

const label = (a, fr) => {
  const sp = a.species ? (fr ? SPECIES_FR[a.species] || a.species : a.species) : "";
  const what = a.breed || sp;
  return { what, sub: [a.breed && sp, a.province ? PROVINCE_NAME[a.province] || a.province : (fr ? "Tout le Canada" : "All of Canada")].filter(Boolean).join(" · ") };
};

const AlertRow = ({ a, fr, master, onToggle, onDelete }) => {
  const { what, sub } = label(a, fr);
  return (
    <li className={`flex items-center gap-3 py-3 ${a.active && master ? "" : "opacity-60"}`} data-testid={`litter-alert-${a.id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[#12234A] truncate">{what}</p>
        <p className="text-xs text-slate-500 truncate">{sub}</p>
      </div>
      <span className="text-xs text-slate-400 w-16 text-right">{a.active ? (fr ? "Active" : "On") : (fr ? "En pause" : "Paused")}</span>
      <Switch checked={a.active} onCheckedChange={(v) => onToggle(a, v)} data-testid={`litter-alert-toggle-${a.id}`} />
      <button onClick={() => onDelete(a)} aria-label="Delete" data-testid={`litter-alert-delete-${a.id}`} className="h-8 w-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors"><Trash2 size={15} /></button>
    </li>
  );
};

export function LitterAlerts() {
  const { lang } = useLanguage();
  const fr = lang === "fr";
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/my/litter-alerts").then((r) => setData(r.data)).catch(() => setData({ alerts: [], emails_on: true })), []);
  useEffect(() => { load(); }, [load]);
  const setMaster = async (v) => {
    setData((d) => ({ ...d, emails_on: v }));
    try { await api.put("/auth/me/preferences", { litter_alert_emails: v }); toast.success(v ? (fr ? "Alertes de portée activées" : "Litter alerts on") : (fr ? "Toutes les alertes de portée en pause" : "All litter alerts paused")); }
    catch (e) { toast.error(errText(e)); load(); }
  };
  const toggle = async (a, v) => {
    setData((d) => ({ ...d, alerts: d.alerts.map((x) => (x.id === a.id ? { ...x, active: v } : x)) }));
    try { await api.patch(`/my/litter-alerts/${a.id}`, { active: v }); } catch (e) { toast.error(errText(e)); load(); }
  };
  const remove = async (a) => {
    try { await api.delete(`/my/litter-alerts/${a.id}`); setData((d) => ({ ...d, alerts: d.alerts.filter((x) => x.id !== a.id) })); toast.success(fr ? "Alerte supprimée" : "Alert deleted"); }
    catch (e) { toast.error(errText(e)); }
  };
  if (!data) return null;
  return (
    <section className="mt-8 bg-white rounded-2xl border border-slate-200/80 p-6" data-testid="litter-alerts-card">
      <div className="flex items-start gap-4">
        <span className="h-10 w-10 rounded-xl bg-[#FFF4F0] flex items-center justify-center shrink-0"><Egg size={20} className="text-[#F5551E]" /></span>
        <div className="flex-1">
          <h3 className="font-display font-bold text-[#12234A] text-lg">{fr ? "Alertes de portée" : "Litter alerts"}</h3>
          <p className="text-sm text-slate-500 mt-0.5">{fr ? "Recevez un courriel dès qu'une nouvelle portée correspondante est annoncée." : "Get an email as soon as a matching new litter or clutch is announced."}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-slate-500 hidden sm:inline">{fr ? "Courriels" : "Emails"}</span>
          <Switch checked={data.emails_on} onCheckedChange={setMaster} data-testid="litter-alerts-master" />
        </div>
      </div>
      {!data.emails_on && data.alerts.length > 0 && <p className="mt-4 text-xs bg-amber-50 text-amber-800 rounded-lg px-3 py-2" data-testid="litter-alerts-paused-note">{fr ? "Toutes vos alertes sont en pause. Réactivez les courriels pour les recevoir." : "All your litter alerts are paused. Turn emails back on to receive them."}</p>}
      {data.alerts.length ? (
        <ul className="mt-3 divide-y divide-slate-100" data-testid="litter-alerts-list">
          {data.alerts.map((a) => <AlertRow key={a.id} a={a} fr={fr} master={data.emails_on} onToggle={toggle} onDelete={remove} />)}
        </ul>
      ) : (
        <p className="mt-4 text-sm text-slate-500" data-testid="litter-alerts-empty">{fr ? "Aucune alerte pour l'instant." : "No alerts yet."}</p>
      )}
      <Link to="/litters" data-testid="litter-alerts-add" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#F5551E] hover:gap-2 transition-all">
        <Plus size={15} /> {fr ? "Créer une alerte depuis Portées à venir" : "Add an alert from Upcoming Litters"}
      </Link>
    </section>
  );
}
